/**
 * Loan aggregate.
 *
 * Money lives in minor units (`amountMinor: number`). For an amount of
 * 250,000.50 in any 100-minor-unit currency, we store 25_000_050. Step 6
 * may migrate to bigint for safer math; for now

 * the constraint is amount stays under Number.MAX_SAFE_INTEGER — fine for
 * any realistic phone loan.
 *
 * `schedule` is the immutable repayment plan locked at contract signature.
 * Re-amortisation (e.g., after a partial early payment) writes a NEW Loan
 * with a NEW schedule and chains the change into the audit log.
 */
import { z } from "zod";
const moneySchema = z.object({
    amountMinor: z.number().int(),
    currency: z.string().length(3),
});
const scheduleEntrySchema = z.object({
    installmentNumber: z.number().int().positive(),
    dueAt: z.coerce.date(),
    principalMinor: z.number().int().nonnegative(),
    interestMinor: z.number().int().nonnegative(),
    feesMinor: z.number().int().nonnegative(),
});
const lockStageSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
]);
const lockStateSchema = z.enum(["UNLOCKED", "SOFT_LOCKED", "HARD_LOCKED", "UNKNOWN"]);
const loanStatusSchema = z.enum([
    "DRAFT",
    "PENDING_VERIFICATION",
    "PENDING_DEPOSIT",
    "PENDING_DEVICE_PROVISIONING",
    "ACTIVE",
    "IN_ARREARS",
    "LOCKED",
    "IN_DISPUTE",
    "PENDING_RELEASE",
    "PAID_OFF",
    "WRITTEN_OFF",
    "REJECTED",
]);
export const loanSchema = z.object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    customerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    deviceId: z.string().uuid(),
    contractId: z.string().uuid(),
    principal: moneySchema,
    deposit: moneySchema,
    schedule: z.array(scheduleEntrySchema).min(1),
    status: loanStatusSchema,
    lockStage: lockStageSchema,
    nextEvalAt: z.coerce.date(),
    desiredLockState: lockStateSchema,
    providerLockState: lockStateSchema,
    deviceConfirmedState: lockStateSchema,
    policyVersionId: z.string().min(1),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export function makeLoan(raw) {
    const parsed = loanSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        customerId: parsed.customerId,
        dealerId: parsed.dealerId,
        deviceId: parsed.deviceId,
        contractId: parsed.contractId,
        principal: {
            amountMinor: parsed.principal.amountMinor,
            currency: parsed.principal.currency,
        },
        deposit: {
            amountMinor: parsed.deposit.amountMinor,
            currency: parsed.deposit.currency,
        },
        schedule: parsed.schedule.map((s) => ({
            installmentNumber: s.installmentNumber,
            dueAt: s.dueAt,
            principalMinor: s.principalMinor,
            interestMinor: s.interestMinor,
            feesMinor: s.feesMinor,
        })),
        status: parsed.status,
        lockStage: parsed.lockStage,
        nextEvalAt: parsed.nextEvalAt,
        desiredLockState: parsed.desiredLockState,
        providerLockState: parsed.providerLockState,
        deviceConfirmedState: parsed.deviceConfirmedState,
        policyVersionId: parsed.policyVersionId,
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
export function isLoanTerminal(loan) {
    return loan.status === "PAID_OFF" || loan.status === "WRITTEN_OFF" || loan.status === "REJECTED";
}
//# sourceMappingURL=loan.js.map